// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Heading, Spacer } from '@storylinerlabs/design';
import { useImmerAtom } from 'jotai/immer';
import { useTranslation } from 'react-i18next';

// import types
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import {
  ReleaseNoteAppType,
  ReleaseNoteAppTypeEnum,
  ReleaseNoteCategory,
  ReleaseNoteCategoryEnum,
} from '../../types';

// import components
import Markdown from '../atoms/RemoteMarkdown';
import LinkComponent from '../atoms/LinkComponent';

// import modules
import { localeAtom } from '../../modules/jotai';

// import styles
import styles from './ReleaseNote.module.css';

// define component
const ReleaseNote: React.FunctionComponent<{
  releaseNote: {
    app: ReleaseNoteAppType;
    category: ReleaseNoteCategory;
    content: MDXRemoteSerializeResult;
    ctaLink?: string | null;
    ctaText?: string | null;
    pubDate: string;
    slug: string;
    title: string;
    type: string;
  };
}> = ({ releaseNote }) => {
  // init hooks
  const [locale] = useImmerAtom(localeAtom);
  const { t } = useTranslation('components');

  // define methods
  const onSerializeDate = React.useCallback(
    (contenfulDate: string) => {
      const options: { year: 'numeric'; month: 'long'; day: 'numeric' } = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      const date = new Date(contenfulDate);
      const dateLocale = locale === 'de' ? 'de-DE' : 'en-US';
      return date?.toLocaleDateString(dateLocale, options);
    },
    [locale],
  );

  // init vars
  const link =
    locale === 'de' ? `/de/changelog/${releaseNote.slug}/` : `/changelog/${releaseNote.slug}/`;
  const title = `#${t(`ReleaseNotes.app.${releaseNote.app}` as const)} #${t(
    `ReleaseNotes.category.${releaseNote.category}` as const,
  )}`;

  // render
  return (
    <div className={styles['container']} key={releaseNote.pubDate}>
      <div className={styles['date']}>
        <LinkComponent href={link}>{onSerializeDate(releaseNote.pubDate)}</LinkComponent>
      </div>
      <div className={styles['content']}>
        <Heading tag="label">{title}</Heading>
        <Spacer height="8px" />
        <Heading tag="h3">{releaseNote.title}</Heading>
        <Markdown content={releaseNote?.content} />
      </div>
    </div>
  );
};

ReleaseNote.propTypes = {
  releaseNote: PropTypes.shape({
    app: PropTypes.oneOf(Object.values(ReleaseNoteAppTypeEnum)).isRequired,
    category: PropTypes.oneOf(Object.values(ReleaseNoteCategoryEnum)).isRequired,
    content: PropTypes.shape({ compiledSource: PropTypes.string.isRequired }).isRequired,
    ctaLink: PropTypes.string,
    ctaText: PropTypes.string,
    pubDate: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
};

ReleaseNote.displayName = 'ReleaseNote';

export default ReleaseNote;
